import { formatBRL, centavosParaReais } from '@pgcustom/shared-utils';
import type { Usuario, RegraComissao, CategoriaServico, TipoComissaoConfiguravel } from '@pgcustom/shared-types';
import type { PgTable, PgTableColumn, PgTableRow, PgKpiCard, PgModal } from '@pgcustom/design-system';
import type { Resultado } from '../../core/resultado.js';
import type { UsuariosApi } from '../usuarios/usuarios.api.js';
import type { ComissoesApi } from '../comissoes/comissoes.api.js';
import type { PermissoesEquipe } from './equipe.permissoes.js';

const COLUNAS: PgTableColumn[] = [
  { key: 'nome', label: 'Nome' },
  { key: 'email', label: 'E-mail' },
  { key: 'papel', label: 'Papel' },
  { key: 'comissao', label: 'Comissão configurada' },
];

const ROTULOS_PAPEL: Record<string, string> = {
  admin: 'Administrador',
  financeiro: 'Financeiro',
  producao: 'Produção',
  parceiros: 'Parceiros',
  marketing: 'Marketing',
  suporte: 'Suporte',
};

const ROTULOS_TIPO_COMISSAO: Record<TipoComissaoConfiguravel, string> = {
  cupom_atribuicao: 'Atribuição de cupom',
  colaborador_global: 'Colaborador (global)',
  servico_producao: 'Serviço de produção',
  assinatura_pg_art: 'Assinatura PG Art',
};

/**
 * `<erp-equipe-page>` — visão consolidada da equipe interna
 * (`identidade.usuarios`, mesma fonte de `<erp-usuarios-page>`)
 * enriquecida com a configuração real de comissão de cada pessoa
 * (`comissoes.regras`/`comissoes.categorias_servico`, migrations
 * `024`/`026` — existiam no backend sem nenhum consumidor no frontend
 * até aqui). Vínculo `Usuario.pessoaId` → `RegraComissao.pessoaId`: já
 * existe no modelo de dados (`Decisão 2`), nunca inventado aqui. Só
 * leitura — criar/editar regra de comissão é fora do escopo desta
 * primeira versão (nenhuma tela hoje faz isso, nem para o próprio
 * módulo Comissões).
 */
export class ErpEquipePage extends HTMLElement {
  private readonly root: ShadowRoot;
  private _usuariosApi: UsuariosApi | null = null;
  private _comissoesApi: ComissoesApi | null = null;
  private _permissoes: PermissoesEquipe = { ler: false };

  private resultadoUsuarios: Resultado<Usuario[]> = { status: 'carregando' };
  private regrasPorPessoa = new Map<string, RegraComissao[]>();
  private categoriasPorId = new Map<string, CategoriaServico>();
  private usuariosAtuais: Usuario[] = [];
  private regrasCarregadas = false;

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  set usuariosApi(valor: UsuariosApi) {
    this._usuariosApi = valor;
    if (this.isConnected) void this.carregar();
  }

  set comissoesApi(valor: ComissoesApi) {
    this._comissoesApi = valor;
    if (this.isConnected) void this.carregarComissoes();
  }

  set permissoes(valor: PermissoesEquipe) {
    this._permissoes = valor;
    if (this.isConnected) this.renderEsqueleto();
  }

  connectedCallback(): void {
    this.renderEsqueleto();
    if (this._usuariosApi) void this.carregar();
    if (this._comissoesApi) void this.carregarComissoes();
  }

  private renderEsqueleto(): void {
    this.root.innerHTML = `
      <style>
        :host { display: block; font-family: var(--pg-font-familia-base, system-ui); }
        h1 { font-family: var(--pg-font-familia-display, system-ui); font-size: var(--pg-font-size-xl, 1.5rem); color: var(--pg-color-texto-primario, #f2f3f5); margin: 0; }
        .subtitulo { font-size: var(--pg-font-size-sm, 0.875rem); color: var(--pg-color-texto-secundario, #9aa1ab); margin-top: 2px; margin-bottom: var(--pg-space-5, 24px); }
        .kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: var(--pg-space-4, 16px); margin-bottom: var(--pg-space-4, 16px); }
        .lista-regras { display: flex; flex-direction: column; gap: var(--pg-space-3, 12px); }
        .regra-item { display: flex; align-items: center; justify-content: space-between; gap: var(--pg-space-3, 12px); padding: var(--pg-space-2, 8px) 0; border-bottom: var(--pg-border-largura, 1px) solid var(--pg-color-borda, #2a2f36); font-size: var(--pg-font-size-sm, 0.875rem); }
        .regra-item:last-child { border-bottom: none; }
        .regra-titulo { color: var(--pg-color-texto-primario, #f2f3f5); }
        .regra-detalhe { color: var(--pg-color-texto-secundario, #9aa1ab); font-size: var(--pg-font-size-xs, 0.75rem); }
        .regra-valor { color: var(--pg-color-dourado, #c9a24b); font-weight: var(--pg-font-weight-medio, 500); white-space: nowrap; }
      </style>
      <div>
        <h1>Equipe</h1>
        <div class="subtitulo">Membros internos e a configuração real de comissão de cada um — clique numa linha para ver o detalhe</div>
      </div>

      <div class="kpis">
        <pg-kpi-card id="kpi-total" label="Total na equipe" icone="usuarios" estado="carregando"></pg-kpi-card>
        <pg-kpi-card id="kpi-ativos" label="Ativos" icone="usuarios" estado="carregando"></pg-kpi-card>
        <pg-kpi-card id="kpi-com-comissao" label="Com comissão configurada" icone="dinheiro" estado="carregando"></pg-kpi-card>
        <pg-kpi-card id="kpi-sem-vinculo" label="Sem vínculo societário" icone="usuarios" estado="carregando"></pg-kpi-card>
      </div>

      <pg-card padding="compacto">
        <div id="equipe"></div>
      </pg-card>

      <pg-modal id="modal-regras">
        <span slot="header" id="modal-regras-titulo">Comissão</span>
        <div id="modal-regras-corpo"></div>
      </pg-modal>
    `;

    this.root.querySelector('#equipe')?.addEventListener('pg-row-click', (evento) => {
      const { index } = (evento as CustomEvent<{ row: PgTableRow; index: number }>).detail;
      const usuario = this.usuariosAtuais[index];
      if (usuario) this.abrirModalRegras(usuario);
    });

    const modal = this.root.querySelector('#modal-regras') as PgModal;
    modal.addEventListener('pg-close', () => modal.removeAttribute('open'));

    this.atualizarTabela();
  }

  private async carregar(): Promise<void> {
    const api = this._usuariosApi;
    if (!api) return;

    if (!this._permissoes.ler) {
      this.resultadoUsuarios = { status: 'sem-permissao' };
      this.atualizarTabela();
      return;
    }

    this.resultadoUsuarios = { status: 'carregando' };
    this.atualizarTabela();
    this.resultadoUsuarios = await api.listar();
    this.atualizarTabela();
  }

  /** Regras + categorias: recursos próprios (`comissoes_regras`/`categorias_servico`), RBAC revalidado pelo backend — indisponibilidade aqui só oculta a coluna "Comissão configurada", nunca a lista de membros em si (fonte separada). */
  private async carregarComissoes(): Promise<void> {
    const api = this._comissoesApi;
    if (!api || !this._permissoes.ler) return;

    const [regrasResultado, categoriasResultado] = await Promise.all([api.listarRegras(), api.listarCategorias()]);

    this.regrasPorPessoa = new Map();
    if (regrasResultado.status === 'ok') {
      for (const regra of regrasResultado.dado) {
        if (regra.vigenciaFim !== null) continue;
        const lista = this.regrasPorPessoa.get(regra.pessoaId) ?? [];
        lista.push(regra);
        this.regrasPorPessoa.set(regra.pessoaId, lista);
      }
    }

    this.categoriasPorId = new Map();
    if (categoriasResultado.status === 'ok') {
      for (const categoria of categoriasResultado.dado) this.categoriasPorId.set(categoria.id, categoria);
    }

    this.regrasCarregadas = regrasResultado.status === 'ok' || regrasResultado.status === 'vazio';
    this.atualizarTabela();
  }

  private atualizarTabela(): void {
    const container = this.root.querySelector('#equipe');
    if (!container) return;

    if (this.resultadoUsuarios.status === 'ok') {
      this.usuariosAtuais = this.resultadoUsuarios.dado;
      container.innerHTML = '<pg-table></pg-table>';
      const tabela = container.querySelector('pg-table') as PgTable;
      tabela.columns = COLUNAS;
      tabela.rows = this.usuariosAtuais.map((u) => ({
        nome: u.nome,
        email: u.email,
        papel: ROTULOS_PAPEL[u.papel] ?? u.papel,
        comissao: this.resumoComissao(u),
      }));
      this.atualizarResumo(this.usuariosAtuais);
      return;
    }

    this.usuariosAtuais = [];
    const { variant, titulo, descricao } = estadoVazio(this.resultadoUsuarios, 'Equipe', 'nenhum membro cadastrado ainda');
    container.innerHTML = `<pg-empty-state variant="${variant}" titulo="${escaparHtml(titulo)}" descricao="${escaparHtml(descricao)}"></pg-empty-state>`;
    for (const id of ['kpi-total', 'kpi-ativos', 'kpi-com-comissao', 'kpi-sem-vinculo']) {
      const kpi = this.root.querySelector(`#${id}`) as PgKpiCard | null;
      if (!kpi) continue;
      if (this.resultadoUsuarios.status === 'vazio') {
        kpi.setAttribute('estado', 'ok');
        kpi.setAttribute('valor', '0');
      } else {
        kpi.setAttribute('estado', variant === 'carregando' ? 'carregando' : 'indisponivel');
      }
    }
  }

  private resumoComissao(usuario: Usuario): string {
    if (!this.regrasCarregadas) return '…';
    if (!usuario.pessoaId) return 'sem vínculo societário';
    const regras = this.regrasPorPessoa.get(usuario.pessoaId) ?? [];
    if (regras.length === 0) return '—';
    return regras.length === 1 ? '1 regra ativa' : `${regras.length} regras ativas`;
  }

  private atualizarResumo(usuarios: Usuario[]): void {
    const ativos = usuarios.filter((u) => u.ativo).length;
    const semVinculo = usuarios.filter((u) => !u.pessoaId).length;
    const comComissao = this.regrasCarregadas
      ? usuarios.filter((u) => u.pessoaId && (this.regrasPorPessoa.get(u.pessoaId)?.length ?? 0) > 0).length
      : null;

    const definir = (id: string, valor: number | null): void => {
      const kpi = this.root.querySelector(`#${id}`) as PgKpiCard | null;
      if (!kpi) return;
      kpi.setAttribute('estado', valor === null ? 'carregando' : 'ok');
      if (valor !== null) kpi.setAttribute('valor', String(valor));
    };

    definir('kpi-total', usuarios.length);
    definir('kpi-ativos', ativos);
    definir('kpi-com-comissao', comComissao);
    definir('kpi-sem-vinculo', semVinculo);
  }

  private abrirModalRegras(usuario: Usuario): void {
    const modal = this.root.querySelector('#modal-regras') as PgModal;
    const titulo = this.root.querySelector('#modal-regras-titulo');
    const corpo = this.root.querySelector('#modal-regras-corpo');
    if (!modal || !titulo || !corpo) return;

    titulo.textContent = `Comissão — ${usuario.nome}`;

    if (!usuario.pessoaId) {
      corpo.innerHTML = `<pg-empty-state variant="vazio" titulo="Sem vínculo societário" descricao="este usuário ainda não está vinculado a uma pessoa em societario.pessoas — vínculo pendente de decisão manual do owner"></pg-empty-state>`;
      modal.setAttribute('open', '');
      return;
    }

    const regras = this.regrasPorPessoa.get(usuario.pessoaId) ?? [];
    if (regras.length === 0) {
      corpo.innerHTML = `<pg-empty-state variant="vazio" titulo="Nenhuma regra ativa" descricao="nenhuma regra de comissão configurada para esta pessoa"></pg-empty-state>`;
      modal.setAttribute('open', '');
      return;
    }

    corpo.innerHTML = `
      <div class="lista-regras">
        ${regras
          .map((regra) => {
            const categoria = regra.categoriaServicoId ? this.categoriasPorId.get(regra.categoriaServicoId) : null;
            const rotuloTipo = ROTULOS_TIPO_COMISSAO[regra.tipoComissao] ?? regra.tipoComissao;
            const detalhe = categoria ? `${rotuloTipo} — ${categoria.nome}` : rotuloTipo;
            const valor =
              regra.modo === 'percentual'
                ? `${regra.percentual ?? 0}%`
                : formatBRL(centavosParaReais(regra.valorFixoCentavos ?? 0));
            return `
              <div class="regra-item">
                <div>
                  <div class="regra-titulo">${escaparHtml(detalhe)}</div>
                  <div class="regra-detalhe">vigente desde ${new Date(regra.vigenciaInicio).toLocaleDateString('pt-BR')}</div>
                </div>
                <span class="regra-valor">${escaparHtml(valor)}</span>
              </div>`;
          })
          .join('')}
      </div>`;
    modal.setAttribute('open', '');
  }
}

function estadoVazio<T>(resultado: Resultado<T>, titulo: string, mensagemVazio: string): { variant: string; titulo: string; descricao: string } {
  switch (resultado.status) {
    case 'carregando':
      return { variant: 'carregando', titulo, descricao: '' };
    case 'sem-permissao':
      return { variant: 'sem-permissao', titulo, descricao: 'você não tem permissão para ver este dado' };
    case 'erro':
      return { variant: 'erro', titulo, descricao: resultado.mensagem };
    default:
      return { variant: 'vazio', titulo: 'Nenhum registro', descricao: mensagemVazio };
  }
}

function escaparHtml(valor: string): string {
  const div = document.createElement('div');
  div.textContent = valor;
  return div.innerHTML;
}

customElements.define('erp-equipe-page', ErpEquipePage);

declare global {
  interface HTMLElementTagNameMap {
    'erp-equipe-page': ErpEquipePage;
  }
}
